'use client'

import { useInViewOnce } from '@/hooks/useInViewOnce'
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion'
import SectionVideo from './SectionVideo'

interface SectionClientProps {
  id: string
  imagePosition?: 'left' | 'right'
  backgroundColor?: string
  title: string
  description: string
  imagePlaceholder: string
  features: string[]
  media?: React.ReactNode
  video?: {
    src: string
    poster?: string
    ariaLabel: string
    priority: boolean
  }
}

const TIMING = {
  content: 200,
  heading: 300,
  description: 400,
  features: 400,
  featureStagger: 100,
}

const revealBaseClass =
  'transition-all duration-700 ease-out will-change-transform'

export default function SectionClient({
  id,
  imagePosition = 'left',
  backgroundColor,
  title,
  description,
  imagePlaceholder,
  features,
  media,
  video,
}: SectionClientProps) {
  const { ref, isInView } = useInViewOnce<HTMLElement>({ threshold: 0.3 })
  const prefersReducedMotion = usePrefersReducedMotion()
  const shouldReveal = prefersReducedMotion || isInView

  return (
    <section
      id={id}
      ref={ref}
      // Let the browser skip heavy offscreen section rendering until scrolled near it.
      className={`py-12 md:py-20 [content-visibility:auto] [contain-intrinsic-size:1px_900px] ${backgroundColor || ''}`}
    >
      <div className="max-w-7xl mx-auto md:px-5">
        <div
          className={`flex flex-col ${
            imagePosition === 'right' ? 'md:flex-row-reverse' : 'md:flex-row'
          } gap-6 md:gap-12 items-center`}
        >
          <div
            className={`flex-1 w-full ${revealBaseClass} ${
              shouldReveal
                ? 'opacity-100 translate-x-0 scale-100'
                : `${imagePosition === 'left' ? '-translate-x-10' : 'translate-x-10'} opacity-0 scale-[0.95]`
            }`}
          >
            {video ? (
              <SectionVideo
                videoSrc={video.src}
                videoPoster={video.poster}
                ariaLabel={video.ariaLabel}
                priority={video.priority}
                isInView={isInView}
              />
            ) : media ? (
              media
            ) : (
              <div className="aspect-video bg-card border border-border rounded-2xl flex items-center justify-center">
                <div className="text-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-16 h-16 mx-auto mb-4 text-muted-foreground"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
                    />
                  </svg>
                  <p className="text-muted-foreground">{imagePlaceholder}</p>
                </div>
              </div>
            )}
          </div>

          <div
            className={`flex-1 px-5 md:px-0 ${imagePosition === 'left' ? 'md:text-right' : ''} ${revealBaseClass} ${
              shouldReveal
                ? 'opacity-100 translate-x-0'
                : `${imagePosition === 'left' ? 'translate-x-10' : '-translate-x-10'} opacity-0`
            }`}
            style={
              prefersReducedMotion
                ? undefined
                : { transitionDelay: `${TIMING.content}ms` }
            }
          >
            <h2
              className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-2 text-heading ${revealBaseClass} ${
                shouldReveal
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-5'
              }`}
              style={
                prefersReducedMotion
                  ? undefined
                  : { transitionDelay: `${TIMING.heading}ms` }
              }
            >
              {title}
            </h2>
            <p
              className={`text-sm sm:text-base md:text-lg text-heading-muted mb-6 max-w-md ${imagePosition === 'left' ? 'md:ml-auto' : ''} ${revealBaseClass} ${
                shouldReveal
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-5'
              }`}
              style={
                prefersReducedMotion
                  ? undefined
                  : { transitionDelay: `${TIMING.description}ms` }
              }
            >
              {description}
            </p>
            <ul className="space-y-3">
              {features.map((feature, index) => (
                <li
                  key={feature}
                  className={`flex items-start gap-3 ${imagePosition === 'left' ? 'md:flex-row-reverse' : ''} ${revealBaseClass} ${
                    shouldReveal
                      ? 'opacity-100 translate-x-0'
                      : 'opacity-0 -translate-x-5'
                  }`}
                  style={
                    prefersReducedMotion
                      ? undefined
                      : {
                          transitionDelay: `${TIMING.features + index * TIMING.featureStagger}ms`,
                        }
                  }
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="w-6 h-6 text-primary shrink-0 mt-0.5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  )
}
